import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { apiCall } from '@/utils/api';

// Types
interface MatrixRow {
  rowId: string;
  isId: string;
  isName: string;
  isCategory: string;
  isDemandTW: number;
  isDemandCost: number;
  epicId: string;
  epicName: string;
  epicEstimateTW: number;
  epicEstimateCost: number;
  epicPriority: number;
  epicStatus: string;
  allocations: {
    [programId: string]: {
      [teamId: string]: {
        allocationId: string;
        teamWeeks: number;
        cost: number;
      };
    };
  };
  totalAllocatedTW: number;
  variance: number;
}

interface ProgramColumn {
  programId: string;
  programName: string;
  programCategory: string;
  colorIndex: number;
  teams: Array<{
    teamId: string;
    teamName: string;
    memberCount: number;
  }>;
}

interface MatrixColumns {
  programs: ProgramColumn[];
}

interface MatrixSummary {
  totalDemandTW: number;
  totalAllocatedTW: number;
  totalVariance: number;
  totalCost: number;
}

interface EditingCell {
  rowId: string;
  programId: string;
  teamId: string;
  currentValue: number;
}

interface MatrixAllocationsState {
  rows: MatrixRow[];
  columns: MatrixColumns;
  summary: MatrixSummary;
  scenarioId: string | null;
  pendingUpdates: Record<string, number>; // allocationId -> teamWeeks
  dirtyCells: Record<string, boolean>; // cellKey -> isDirty
  editingCell: EditingCell | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
}

const initialState: MatrixAllocationsState = {
  rows: [],
  columns: { programs: [] },
  summary: {
    totalDemandTW: 0,
    totalAllocatedTW: 0,
    totalVariance: 0,
    totalCost: 0,
  },
  scenarioId: null,
  pendingUpdates: {},
  dirtyCells: {},
  editingCell: null,
  isLoading: false,
  isSaving: false,
  error: null,
};

// Async thunks
export const fetchMatrixAllocations = createAsyncThunk(
  'matrixAllocations/fetch',
  async ({ scenarioId }: { scenarioId?: string | null } = {}) => {
    const params = new URLSearchParams();
    if (scenarioId) params.append('scenarioId', scenarioId);

    const response = await apiCall('GET', `/allocations/matrix?${params}`);
    return response.data?.data || response.data;
  }
);

export const bulkSaveAllocations = createAsyncThunk(
  'matrixAllocations/bulkSave',
  async (updates: Array<{ id: string; teamWeeks: number }>) => {
    const response = await apiCall('PUT', '/allocations/bulk-update', { updates });
    return response.data?.data || response.data;
  }
);

// Slice
const matrixAllocationsSlice = createSlice({
  name: 'matrixAllocations',
  initialState,
  reducers: {
    setScenarioId(state, action: PayloadAction<string | null>) {
      state.scenarioId = action.payload;
    },

    startEditingCell(
      state,
      action: PayloadAction<{
        rowId: string;
        programId: string;
        teamId: string;
        currentValue: number;
      }>
    ) {
      state.editingCell = action.payload;
    },

    updateEditingCellValue(state, action: PayloadAction<number>) {
      if (!state.editingCell) return;

      const { rowId, programId, teamId, currentValue } = state.editingCell;
      const newValue = action.payload;

      // Find the row and update allocation
      const row = state.rows.find((r) => r.rowId === rowId);
      if (row?.allocations[programId]?.[teamId]) {
        const allocationId = row.allocations[programId][teamId].allocationId;

        // Optimistic update
        row.allocations[programId][teamId].teamWeeks = newValue;

        // Track pending update
        if (newValue !== currentValue) {
          state.pendingUpdates[allocationId] = newValue;

          // Mark dirty
          const cellKey = `${rowId}-${programId}-${teamId}`;
          state.dirtyCells[cellKey] = true;
        }

        // Recalculate row totals
        row.totalAllocatedTW = Object.values(row.allocations).reduce((sum, teams) => {
          return sum + Object.values(teams).reduce((tSum, alloc) => tSum + alloc.teamWeeks, 0);
        }, 0);
        row.variance = row.epicEstimateTW - row.totalAllocatedTW;

        // Recalculate summary
        state.summary.totalAllocatedTW = state.rows.reduce((sum, r) => sum + r.totalAllocatedTW, 0);
        state.summary.totalVariance = state.summary.totalDemandTW - state.summary.totalAllocatedTW;
      }
    },

    saveEditingCell(state) {
      state.editingCell = null;
    },

    cancelEditingCell(state) {
      // Revert the change if we were editing
      if (state.editingCell) {
        const { rowId, programId, teamId, currentValue } = state.editingCell;
        const row = state.rows.find((r) => r.rowId === rowId);
        if (row?.allocations[programId]?.[teamId]) {
          row.allocations[programId][teamId].teamWeeks = currentValue;

          // Remove from pending updates
          const allocationId = row.allocations[programId][teamId].allocationId;
          delete state.pendingUpdates[allocationId];

          // Remove dirty flag
          const cellKey = `${rowId}-${programId}-${teamId}`;
          delete state.dirtyCells[cellKey];
        }
      }
      state.editingCell = null;
    },

    clearPendingUpdates(state) {
      state.pendingUpdates = {};
      state.dirtyCells = {};
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchMatrixAllocations.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMatrixAllocations.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload) {
          state.rows = action.payload.rows || [];
          state.columns = action.payload.columns || { programs: [] };
          state.summary = action.payload.summary || {
            totalDemandTW: 0,
            totalAllocatedTW: 0,
            totalVariance: 0,
            totalCost: 0,
          };
        }
        state.pendingUpdates = {};
        state.dirtyCells = {};
      })
      .addCase(fetchMatrixAllocations.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch matrix allocations';
      })
      .addCase(bulkSaveAllocations.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(bulkSaveAllocations.fulfilled, (state) => {
        state.isSaving = false;
        state.pendingUpdates = {};
        state.dirtyCells = {};
      })
      .addCase(bulkSaveAllocations.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.error.message || 'Failed to save allocations';
      });
  },
});

export const {
  setScenarioId,
  startEditingCell,
  updateEditingCellValue,
  saveEditingCell,
  cancelEditingCell,
  clearPendingUpdates,
} = matrixAllocationsSlice.actions;

export default matrixAllocationsSlice.reducer;
